import Vue from "vue";
import VueRouter from "vue-router";
import routes from "./routes";
import VueMeta from "vue-meta";
import store from "@/state/store";
import { Auth } from "aws-amplify";
import ApiService from '@/helpers/api.service'

const API = new ApiService()

Vue.use(VueRouter);

Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: "page",
});
const router = new VueRouter({
  routes,
  mode: "history",
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {
    // Check if auth is required on this route
    // (including nested routes).
    const authRequired = routeTo.matched.some(
      (route) => route.meta.authRequired
    );

    // If auth isn't required for the route, just continue.
    if (!authRequired) return next();

    // If auth is required and the user is logged in...
    if (store.getters["auth/loggedIn"]) {
      // Validate the local user token...
      return store.dispatch("auth/validate").then((validUser) => {
        // Then continue if the token still represents a valid user,
        // otherwise redirect to login.
        validUser ? next() : redirectToLogin();
      });
    }

    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin();

    // eslint-disable-next-line no-unused-vars
    // eslint-disable-next-line no-inner-declarations
    function redirectToLogin() {
      // Pass the original route to the login component
      next({ name: "login", query: { redirectFrom: routeTo.fullPath } });
    }
  } else if (process.env.VUE_APP_DEFAULT_AUTH === "fakebackend") {
    const publicPages = ["/login", "/register", "/forgot-password"];
    const authpage = !publicPages.includes(routeTo.path);
    const loggeduser = JSON.parse(localStorage.getItem("userData"));
    try {// this will handle the action taken by approver from email PEDDLR-312
      var email_action = {
        "module" :  new URL(location.href).searchParams.get('module'),
        "action" :  new URL(location.href).searchParams.get('action'),
        "token" :  new URL(location.href).searchParams.get('token'),
      };
      if(email_action["action"] != null && email_action["token"] != null){//set eload reward details first
        localStorage.setItem("email_action", JSON.stringify(email_action));
      }
    } catch (error) {
      console.log(">>> Err:",error);
    }
    if (authpage && !loggeduser) {
      return next(`/login?redirect_link=${window.location.href}`);
    } else if (authpage && (loggeduser !== null && loggeduser !== undefined)) {
      
      const time = Date.now()
      let timenow = Math.floor(time / 1000)
      
      // Check if token is expired
      if (loggeduser.expirationTime && loggeduser.expirationTime - timenow <= 0) {
        // Token expired, sign out and redirect to login
        Auth.signOut()
          .then(() => {
            console.log("Token expired - SignOut")
            sessionStorage.removeItem('authUser');
            localStorage.removeItem('userData');
            return next(`/login?redirect_link=${window.location.href}`);
          })
          .catch(err => {
            console.log("Error signing out: " + err);
            sessionStorage.removeItem('authUser');
            localStorage.removeItem('userData');
            return next(`/login?redirect_link=${window.location.href}`);
          });
      } else if (loggeduser.expirationTime && loggeduser.expirationTime - timenow > 0) {
        // Token still valid, try to refresh
        Auth.currentAuthenticatedUser()
          .then(async (user) => {
            console.log("Refreshing access and id tokens");
            let currentSession = user.signInUserSession
            await user.refreshSession(currentSession.refreshToken, async (err, session) => {
              if (err) {
                console.log("Error refreshing tokens: " + err);
                Auth.signOut()
                  .then(() => {
                    console.log("SignOut after refresh error")
                    sessionStorage.removeItem('authUser');
                    localStorage.removeItem('userData');
                    return next(`/login?redirect_link=${window.location.href}`);
                  })
                  .catch(err => {
                    console.log("Error signing out: " + err);
                    sessionStorage.removeItem('authUser');
                    localStorage.removeItem('userData');
                    return next(`/login?redirect_link=${window.location.href}`);
                  });
              } else {
                let userData = JSON.parse(localStorage.getItem('userData'))
                let tmp_userData = {
                  ...userData,
                  jwtToken: session.idToken.jwtToken,
                  expirationTime: session.idToken.payload.exp
                }
                localStorage.setItem('userData', JSON.stringify(tmp_userData))
                await API.setHeaders(session.idToken.jwtToken)
                next()
              }
            })
          })
          .catch(err => {
            console.log("Auth.currentAuthenticatedUser(): " + err);
            // User not authenticated, clear storage and redirect
            sessionStorage.removeItem('authUser');
            localStorage.removeItem('userData');
            return next(`/login?redirect_link=${window.location.href}`);
          });
      } else {
        // No expiration time set, allow through but this shouldn't happen
        next();
      }
    } else {
      next();
    }
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // If we reach this point, continue resolving the route.
  next();
});

export default router;
